<template>
  <div class="bulk-import">
    <b-container>
      <page-title icon="fas fa-boxes">
        <translate>Bulk Shipments Import</translate>
      </page-title>

      <p class="my-3" v-translate>Here you can bulk import many shipments at once. To do it you must read the <a href="https://ayuda.moova.io/support/solutions/articles/70000374919-como-importar-env%C3%ADos-desde-un-excel-o-csv-desde-el-dashboard-de-moova" target="_blank">extended instructions</a> first. There you will be able to download the Bulk Import Template that better match your requirements. After that you can populate the document with your data, export/save it as Microsoft Excel XLSX, upload it using the box below and follow the showed instructions. If everything goes alright that would be all 😊</p>
      <b-form-row>
        <b-col>
          <label for="reference" v-translate>Company</label>
          <company-autocomplete v-if="!company" ref="companyAc" @selected="onSearchCompanySelected" :includes="['applications']" />
          <b-card v-if="company">
            <span class="bg-danger text-white d-block text-center" style="cursor: pointer; width: 1rem; height: 1rem; line-height: 1rem; position: absolute; top: 0; left: 0" href="#" v-if="(preCheckedShippings.length === 0 && !checking)" @click.prevent="company = null; defaultShippingFields = {contact: {}}; validApp = profile.company.applications.find(x => x.platform_id === null) || profile.company.applications.find(x => x.platform.name === 'Moova')">×</span>
            <company-card :company="company" profilePictureSize="110px" />
          </b-card>
        </b-col>
      </b-form-row>
      <b-form-file
        v-model="file"
        :placeholder="this.$gettext('Choose a Moova standard Bulk Import Template or drop it here...')"
        :drop-placeholder="this.$gettext('Drop file here...')"
        @input="fileChanged"
        ref="importedShippings"
        id="importedShippings"
        accept=".csv,.xls,.xlsx"
        class="my-3"
        v-if="!file"
      ></b-form-file>
      <div v-if="checking" class="text-center my-5">
        <b-spinner style="width: 3rem; height: 3rem;"></b-spinner>
        <h5 v-if="checkingSizes" v-translate>Checking Sizes</h5>
        <h5 v-if="checkingAddresses" v-translate>Checking Addresses</h5>
        <h5 v-if="checkingQuotes" v-translate>Checking Quotes</h5>
        <h5 v-if="creatingShippings" v-translate>Creating Shipments</h5>
        <h4>{{checkingNumber}}/{{checkingTotal}}</h4>
      </div>
      <div class="my-3 text-center">
        <b-button size="sm" variant="danger" @click.prevent="preResetImport" v-if="fatalErrors || (preCheckedShippings.length > 0 && !checking)" v-translate>
          Restart Import
        </b-button>
      </div>
      <b-alert variant="danger" :show="fatalErrors">
        <translate>Fatal ERROR (empty file, wrong file type or none of required fields)</translate>
      </b-alert>
      <b-alert variant="success" :show="preCheckedShippings.length > 0 && Boolean(sizeErrors) === false && Boolean(meliErrors) === false && Boolean(fromErrors) === false && Boolean(toErrors) === false && Boolean(quoteErrors) === false && !checking">
        <b-row>
          <b-col class="align-self-center">
            <h4 class="m-0" v-show="step === 2" v-translate>Everything looks alright! Just one more step left! 😊</h4>
            <h4 class="m-0" v-show="step === 3" v-translate>Everything is ready for the import! Go and press the button! 😃</h4>
          </b-col>
          <b-col cols="4">
            <div class="text-right">
              <b-button variant="success" @click.prevent="checkQuotes" v-if="step === 2 && !this.ecosystemConfig.bulkImportSkipSteps || this.ecosystemConfig.bulkImportSkipSteps && this.ecosystemConfig.bulkImportSkipSteps.indexOf('checkQuotes') === -1" v-translate>
                Check Quotes
              </b-button>
              <b-button variant="success" @click.prevent="createShippings" v-if="fromErrors === 0 && toErrors === 0 && quoteErrors === 0 && (step === 3 || (this.ecosystemConfig.bulkImportSkipSteps && this.ecosystemConfig.bulkImportSkipSteps.indexOf('checkQuotes') !== -1 && step === 2))" v-translate>
                Create Shipments!
              </b-button>
            </div>
          </b-col>
        </b-row>
      </b-alert>

      <b-alert variant="warning" :show="preCheckedShippings.length > 0 && (Boolean(sizeErrors) === true || Boolean(meliErrors) === true || Boolean(fromErrors) === true || Boolean(toErrors) === true || Boolean(quoteErrors) === true) && !checking">
        <h5 v-translate>There are some errors and/or missing data to review before continue</h5>
        <ul>
          <!-- scan setting errors -->
          <li v-if="Boolean(scanErrors) && scanErrors === preCheckedShippings.length">
            <translate>All the shipments have the "scan serial number" setting but don't have at least one item and can't be edited here. To continue you must:</translate>
            <ul>
              <li><a href="#" @click.prevent="deleteAllWithErrors('scan')" v-translate>Delete all the rows with this problem. (Will download a CSV with the deleted rows for further revision.)</a></li>
            </ul>
          </li>
          <li v-if="Boolean(scanErrors) && scanErrors !== preCheckedShippings.length">
            <translate>Some shipments have the "scan serial number" setting but don't have at least one item and can't be edited here. To continue you must:</translate>
            <ul>
              <li><a href="#" @click.prevent="deleteAllWithErrors('scan')" v-translate>Delete all the rows with this problem. (Will download a CSV with the deleted rows for further revision.)</a></li>
            </ul>
          </li>
          <!-- size errors -->
          <li v-if="Boolean(sizeErrors) && sizeErrors === preCheckedShippings.length">
            <translate>All sizes are TOO BIG and can't be edited here. To continue you must:</translate>
            <ul>
              <li><a href="#" @click.prevent="deleteAllWithErrors('size')" v-translate>Delete all the rows with this problem. (Will download a CSV with the deleted rows for further revision.)</a></li>
            </ul>
          </li>
          <li v-if="Boolean(sizeErrors) && sizeErrors !== preCheckedShippings.length">
            <translate>Some sizes are TOO BIG and can't be edited here. To continue you must:</translate>
            <ul>
              <li><a href="#" @click.prevent="deleteAllWithErrors('size')" v-translate>Delete all the rows with this problem. (Will download a CSV with the deleted rows for further revision.)</a></li>
            </ul>
          </li>
          <!-- meli errors -->
          <li v-if="Boolean(meliErrors) && meliErrors === preCheckedShippings.length">
            <translate>All the shipments have type "mercado_flex" but don't have their ML shipment number and can't be edited here. To continue you must:</translate>
            <ul>
              <li><a href="#" @click.prevent="deleteAllWithErrors('meli')" v-translate>Delete all the rows with this problem. (Will download a CSV with the deleted rows for further revision.)</a></li>
            </ul>
          </li>
          <li v-if="Boolean(meliErrors) && meliErrors !== preCheckedShippings.length">
            <translate>Some shipments have type "mercado_flex" but don't have their ML shipment number and can't be edited here. To continue you must:</translate>
            <ul>
              <li><a href="#" @click.prevent="deleteAllWithErrors('meli')" v-translate>Delete all the rows with this problem. (Will download a CSV with the deleted rows for further revision.)</a></li>
            </ul>
          </li>
          <!-- address from errors -->
          <li v-if="Boolean(fromErrors) && fromErrors === preCheckedShippings.length">
            <translate>All origins are WRONG. You may want to:</translate>
            <ul>
              <li><a href="#" @click.prevent="deleteAllWithErrors('from')" v-translate>Delete all the rows with this problem. (Will download a CSV with the deleted rows for further revision.)</a></li>
              <li><a href="#" @click.prevent="checkpoint = 'from'" v-b-modal.modalConfirmation v-translate>Set the same origin data for all the rows with this problem.</a></li>
              <li v-translate>Manually edit every row with this problem.</li>
            </ul>
          </li>
          <li v-if="Boolean(fromErrors) && fromErrors !== preCheckedShippings.length">
            <translate>Some origins are WRONG. You may want to:</translate>
            <ul>
              <li><a href="#" @click.prevent="deleteAllWithErrors('from')" v-translate>Delete all the rows with this problem. (Will download a CSV with the deleted rows for further revision.)</a></li>
              <li><a href="#" @click.prevent="checkpoint = 'from'" v-b-modal.modalConfirmation v-translate>Set the same origin data for all the rows with this problem.</a></li>
              <li v-translate>Manually edit every row with this problem.</li>
            </ul>
          </li>
          <!-- address to errors -->
          <li v-if="Boolean(toErrors) && toErrors === preCheckedShippings.length">
            <translate>All destinations are WRONG. You may want to:</translate>
            <ul>
              <li><a href="#" @click.prevent="deleteAllWithErrors('to')" v-translate>Delete all the rows with this problem. (Will download a CSV with the deleted rows for further revision.)</a></li>
              <li><a href="#" @click.prevent="checkpoint = 'to'" v-b-modal.modalConfirmation v-translate>Set the same destination data for all the rows with this problem.</a></li>
              <li v-translate>Manually edit every row with this problem.</li>
            </ul>
          </li>
          <li v-if="Boolean(toErrors) && toErrors !== preCheckedShippings.length">
            <translate>Some destinations are WRONG. You may want to:</translate>
            <ul>
              <li><a href="#" @click.prevent="deleteAllWithErrors('to')" v-translate>Delete all the rows with this problem. (Will download a CSV with the deleted rows for further revision.)</a></li>
              <li><a href="#" @click.prevent="checkpoint = 'to'" v-b-modal.modalConfirmation v-translate>Set the same destination data for all the rows with this problem.</a></li>
              <li v-translate>Manually edit every row with this problem.</li>
            </ul>
          </li>
          <!-- quote errors -->
          <li v-if="Boolean(quoteErrors) && quoteErrors === preCheckedShippings.length">
            <translate>There are no quotes for NONE of your shipments. You may want to:</translate>
            <ul>
              <li v-translate>Re-check your Excel/CSV file origins and destinations and upload again.</li>
              <li v-translate>Contact us for <a href="mailto:help@moova.io">help</a> checking your company quotes.</li>
            </ul>
          </li>
          <li v-if="Boolean(quoteErrors) && quoteErrors !== preCheckedShippings.length">
            <translate>There are no quotes for SOME of your shippings. You may want to:</translate>
            <ul>
              <li><a href="#" @click.prevent="deleteAllWithErrors('quote')" v-translate>Delete all the rows with this problem. (Will download a CSV with the deleted rows for further revision.)</a></li>
              <li v-translate>Contact us for <a href="mailto:help@moova.io">help</a> checking your company quotes.</li>
            </ul>
          </li>
        </ul>

      </b-alert>
    </b-container>
    <b-table striped
      :items="preCheckedShippings"
      :fields="fields"
      stacked="lg"
      v-if="preCheckedShippings.length > 0 && !checking"
    >
      <template v-slot:cell(size)="item">
        <div class="text-lg-center">
          <div v-if="item.item.validSize">
            {{item.value.toUpperCase()}}
          </div>
          <div v-else>
            <font-awesome-icon :icon="['far', 'times-circle']" size="2x" class="text-danger" :id="'tooltip-invalid-size-' + item.index" />
            <b-tooltip :target="'tooltip-invalid-size-' + item.index">
              <translate>Size is too big! You should separate the items in more than one shipments if it is possible. (It can't be done here but in your spreadsheet)</translate>
            </b-tooltip>
          </div>
        </div>
      </template>
      <template v-slot:cell(from)="item">
        <div class="checkpoint from">
          <div v-if="item.value.contact.firstName || item.value.contact.lastName">
            <font-awesome-icon icon="user" size="1x" /> <strong>{{item.value.contact.firstName}} {{item.value.contact.lastName}}</strong>
          </div>
          <div v-if="item.value.contact.phone">
            <font-awesome-icon icon="phone" size="1x" /> <strong>{{preFormatPhone(item.value.contact.phone) | phone($store.getters.profile.company.country_iso_code)}}</strong>
          </div>
          <div v-if="item.value.checkedStringAddress" class="text-warning"><font-awesome-icon icon="exclamation-triangle" size="1x" /> {{item.value.checkedStringAddress}}</div>
          <div v-if="!item.value._editing" :class="item.item.validFrom ? 'text-success' : 'text-danger'"><font-awesome-icon icon="map-marker-alt" size="1x" /> {{item.value.address || $gettext('There are missing REQUIRED fields')}}
            <b-button size="sm" variant="link" @click.prevent="item.value._editing = true" style="padding: 0 .25rem" v-if="item.value.checkedStringAddress || !item.item.validFrom">
              <font-awesome-icon icon="edit" />
            </b-button>
          </div>
          <div v-else>
            <address-autocomplete :pre-query="item.value.address" :ref="'address_from_' + item.index " @selected="selectedAddress($event, 'from', item.index)" static-items :min-character="6" class="d-inline-block" style="width: 100%; max-width: 300px;" />
            <b-button variant="danger" @click.prevent="item.value._editing = false" class="d-inline-block" style="padding: .25rem .65rem">×</b-button>
          </div>
          <div v-if="item.value.floor || item.value.apartment">
            <font-awesome-icon size="1x" icon="crosshairs" /> {{item.value.floor}} {{item.value.apartment}}
          </div>
          <div v-if="item.value.instructions">
            <em>{{item.value.instructions}}</em>
          </div>
        </div>

        <b-button size="sm" v-b-modal.modalEditData @click.prevent="item.value._editing = true; checkpoint = 'from'; itemEdit = JSON.parse(JSON.stringify(item.item))" variant="primary" style="padding: 0.1rem 1rem;" v-translate>Edit origin data</b-button>

      </template>
      <template v-slot:cell(to)="item">
        <div class="checkpoint to">
          <div v-if="item.value.contact.firstName || item.value.contact.lastName">
            <font-awesome-icon icon="user" size="1x" /> <strong>{{item.value.contact.firstName}} {{item.value.contact.lastName}}</strong>
          </div>
          <div v-if="item.value.contact.phone">
            <font-awesome-icon icon="phone" size="1x" /> <strong>{{preFormatPhone(item.value.contact.phone) | phone($store.getters.profile.company.country_iso_code)}}</strong>
          </div>
          <div v-if="item.value.checkedStringAddress" :class="item.item.validTo ? 'text-warning' : 'text-danger'">
            <font-awesome-icon icon="exclamation-triangle" size="1x" />
            {{item.value.checkedStringAddress}}
            <b-button size="sm" variant="primary" @click.stop="openMapOn(item.value.checkedStringAddress)" style="padding: .05rem .25rem; vertical-align: bottom" :title="$gettext('Quick view address map on new window')">
              <font-awesome-icon :icon="['fa', 'map-marked-alt']" size="2x" style="font-size: 10px;vertical-align: middle" />
            </b-button>
          </div>
          <div v-if="!item.value._editing" :class="item.item.validTo ? 'text-success' : 'text-danger'">
            <font-awesome-icon icon="map-marker-alt" size="1x" />
            {{item.value.address || $gettext('There are missing REQUIRED fields')}}
            <b-button size="sm" variant="link" @click.prevent="item.value._editing = true" style="padding: 0 .25rem" v-if="item.value.checkedStringAddress || !item.item.validTo">
              <font-awesome-icon icon="edit" />
            </b-button>
            <b-button size="sm" variant="primary" v-if="item.value.address" @click.stop="openMapOn(item.value.address)" style="padding: .05rem .25rem; vertical-align: bottom" :title="$gettext('Quick view address map on new window')">
              <font-awesome-icon :icon="['fa', 'map-marked-alt']" size="2x" style="font-size: 10px;vertical-align: middle" />
            </b-button>
          </div>
          <div v-else>
            <address-autocomplete :pre-query="item.value.address" :ref="'address_to_' + item.index " @selected="selectedAddress($event, 'to', item.index)" :min-character="6" class="d-inline-block" style="width: 100%; max-width: 300px;" />
            <b-button variant="danger" @click.prevent="item.value._editing = false" class="d-inline-block" style="padding: .25rem .65rem">×</b-button>
          </div>
          <div v-if="item.value.floor || item.value.apartment">
            <font-awesome-icon size="1x" icon="crosshairs" /> {{item.value.floor}} {{item.value.apartment}}
          </div>
          <div v-if="item.value.instructions">
            <em>{{item.value.instructions}}</em>
          </div>
        </div>


        <b-button size="sm" v-b-modal.modalEditData @click.prevent="item.value._editing = true; checkpoint = 'to'; itemEdit = JSON.parse(JSON.stringify(item.item))" variant="primary" style="padding: 0.1rem 1rem;" v-translate>Edit destination data</b-button>


      </template>
      <template v-slot:cell(items)="item">
        <ul class="pl-3">
          <li v-for="(item, index) in item.value" :key="index">
            <div v-if="item.description">
              <span v-translate="{description: item.description}">Description: %{description}</span>
            </div>
            <div v-if="item.reference_code">
              <span v-translate="{referenceCode: item.reference_code}">Reference Code: %{referenceCode}</span>
            </div>
            <div v-if="item.weight">
              <span v-translate="{weight: readableMetric(item.weight)}">Weight: %{weight}</span>
            </div>
            <div v-if="item.width">
              <span v-translate="{width: item.width, height: item.height, length: item.length}">Size: %{width}×%{height}×%{length}cm</span>
            </div>
            <div>
              <span v-translate="{qty: item.quantity}">Quantity: %{qty}</span>
            </div>
          </li>
        </ul>
      </template>
      <template v-slot:cell(internalCode)="item">
        <div class="text-lg-center">
          <div v-if="item.item.type === 'mercado_flex' && !item.value">
            <font-awesome-icon :icon="['far', 'times-circle']" size="2x" class="text-danger" :id="'tooltip-invalid-meli-' + item.index" />
            <b-tooltip :target="'tooltip-invalid-meli-' + item.index">
              <translate>This shipment has type "mercado_flex" but doesn't have the required ML shipment number! You must change the shipping type or add the number in the appropriate column. (It can't be done here but in your spreadsheet)</translate>
            </b-tooltip>
          </div>
          <span v-else>{{item.value}}</span>
        </div>
      </template>
      <template v-slot:cell(internalOrderId)="item">
        <div class="text-lg-center">
          <span>{{item.value}}</span>
        </div>
      </template>
      <template v-slot:cell(settings)="item">
        <div v-for="(setting, i) in item.value" :key="i" class="text-nowrap">
          <span v-if="shippingSettings.find(x => x.value === setting).text === 'UpdateDeliveredItems' && item.item.validScan === false">
            <font-awesome-icon :icon="['far', 'times-circle']" size="1x" class="text-danger" :id="'tooltip-invalid-scan-' + item.index" />
            <b-tooltip :target="'tooltip-invalid-scan-' + item.index">
              <translate>This shipment has the "scan serial number" setting but don't have at least one item! You must change the "scan serial number" setting value or add at least one item in the appropriate columns. (It can't be done here but in your spreadsheet)</translate>
            </b-tooltip>
          </span>
          <font-awesome-icon :icon="['far', 'check-circle']" size="1x" class="text-success" v-else />
          {{ $gettext(shippingSettings.find(x => x.value === setting).text) }}
        </div>
        <div class="d-none">
          <translate>BackAndForth</translate>
          <translate>UpdateDeliveredItems</translate>
        </div>
      </template>
      <template v-slot:cell(validQuote)="item">
        <div class="text-lg-center">
          <font-awesome-icon :icon="['far', 'check-circle']" size="2x" class="text-success" v-if="item.item.validQuote" />
          <font-awesome-icon :icon="['far', 'times-circle']" size="2x" class="text-danger" v-if="item.item.validQuote === false" />
          <strong v-if="item.item.price"><br>{{item.item.price}}</strong>
        </div>
      </template>
      <template v-slot:cell(actions)="item">
        <b-button size="sm" variant="danger" @click.prevent="deleteRow(preCheckedShippings.findIndex(x => x.line === item.item.line))" style="padding: .5rem .65rem" :id="'tooltip-delete-' + item.index">
          <font-awesome-icon icon="trash-alt" size="lg" />
        </b-button>
        <b-tooltip :target="'tooltip-delete-' + item.index">
          <translate>Delete this shipment from the current import and download it as CSV for further revision</translate>
        </b-tooltip>
      </template>
    </b-table>

    <b-modal size="lg" id="modalConfirmation" ref="modalConfirmation" hide-footer @show="resetEditing">
      <div slot="modal-title">
        <span v-show="checkpoint === 'from'" v-translate>Set all WRONG origins</span>
        <span v-show="checkpoint === 'to'" v-translate>Set all WRONG destinations</span>
      </div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <shipping-checkpoint-form :shipping="{}" new-address @cancel="closeModalConfirmation" @success="successModalConfirmation" checkpoint="from" />
    </b-modal>

    <b-modal size="xl" id="modalFromPickPlace" ref="modalFromPickPlace" hide-footer hide-header @hide="$refs.pickFromPlaceMap.pickPlace(0, 0)">
      <place-picker ref="pickFromPlaceMap" hasReference @cancel="closeModalFromPickPlace" @placePicked="setPlacePickedFrom" centerMap fit-always :zoom="17">
        <template v-slot:instructions>
          <p class="mt-3"><translate>Please, click on the map to pick the Origin point. You may also add a text reference or description to help the courier to find out the place.</translate></p>
        </template>
      </place-picker>
    </b-modal>

    <b-modal id='createShippingSuccessMessage' ref='createShippingSuccessMessage' size="md" hide-footer>
      <div slot="modal-title" v-translate>Shipments Created</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <h3 class="my-5 text-center" v-translate="{qty: shippingsCreated.length}" :translate-n="shippingsCreated.length" translate-plural="%{qty} shipments were created!">%{qty} shipment was created!</h3>
      <div class="text-center" v-if="shippingsCreated.length < 40">
        <b-button variant="success" :to="{name: 'ShippingsList', query: {qf: JSON.stringify({'shipping-id': shippingsCreated.map(x => x.link).join('\n')})}}" target="_blank" v-translate>
          Open the Imported Shipments in the Shipment List
        </b-button>
      </div>
      <div class="text-center" v-else>
        <b-button variant="success" :to="{name: 'ShippingsList'}" target="_blank" v-translate>
          Open the Shipment List
        </b-button>
      </div>
    </b-modal>

    <b-modal size="lg" id="modalEditData" ref="modalEditData" hide-footer @show="_editing = true" @hide="preCheckedShippings.find(x => x.line === itemEdit.line)[checkpoint]._editing = null; checkpoint = null; itemEdit = {}">
      <div slot="modal-title">
        <translate v-if="checkpoint === 'from'">Edit Origin Data</translate>
        <translate v-if="checkpoint === 'to'">Edit Destination Data</translate>
      </div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <b-form @submit.stop.prevent="editItem" v-if="itemEdit[checkpoint]">
        <b-form-row>
          <b-form-group class="col-sm-4">
            <label for="from_extra" v-translate>First Name</label>
            <b-form-input type="text" v-model="itemEdit[checkpoint].contact.firstName"></b-form-input>
          </b-form-group>
          <b-form-group class="col-sm-4">
            <label for="from_extra" v-translate>Last Name</label>
            <b-form-input type="text" v-model="itemEdit[checkpoint].contact.lastName"></b-form-input>
          </b-form-group>
          <b-form-group class="col-sm-4">
            <label for="from_extra" v-translate>Phone</label>
            <b-form-input type="text" v-model="itemEdit[checkpoint].contact.phone"></b-form-input>
          </b-form-group>
          <b-form-group class="col-6">
            <label for="from_extra" v-translate>Street and number</label>
            <address-autocomplete :pre-query="itemEdit[checkpoint].address" :ref="'address_' + checkpoint + '_undefined'" :static-items="checkpoint === 'from'" @selected="selectedAddress($event, checkpoint)" :min-character="4" />
          </b-form-group>
          <b-form-group class="col-sm-3">
            <label for="from_extra" v-translate>Floor</label>
            <b-form-input type="text" v-model="itemEdit[checkpoint].floor"></b-form-input>
          </b-form-group>
          <b-form-group class="col-sm-3">
            <label for="from_extra" v-translate>Apartment, door</label>
            <b-form-input type="text" v-model="itemEdit[checkpoint].apartment"></b-form-input>
          </b-form-group>
          <b-form-group class="col-sm-12">
            <label for="from_extra" v-translate>Instructions</label>
            <b-form-textarea type="text" v-model="itemEdit[checkpoint].instructions"></b-form-textarea>
          </b-form-group>
        </b-form-row>
        <b-form-row>
          <b-col class="text-center">
            <b-button type="submit" variant="primary" size="lg" v-translate>Edit Data</b-button>
            <b-button type="button" variant="outline-primary" size="lg" @click.prevent="$refs.modalEditData.hide()" v-translate>Cancel</b-button>
          </b-col>
        </b-form-row>

      </b-form>
    </b-modal>

    <div class="modal-need-application" v-if="!validApp">
      <div class="modal-dialog modal-dialog-centered">
        <b-container fluid class="modal-content p-4">
          <b-row class="justify-content-md-center">
            <b-col cols="12">
              <div v-if="!isGcmAdmin">
                <h5 v-translate>Hi!</h5>
                <p v-translate>Sorry, the Bulk Import system need to be accessed by a Company Administrator for the first time 😬</p>
                <p v-translate>Please ask your Company Administrator to enter this page to get the tool properly configured.</p>
              </div>
              <div v-else>
                <div class="text-center my-5">
                  <b-spinner style="width: 3rem; height: 3rem;"></b-spinner>
                  <h5 v-translate>Configuring Bulk Import...</h5>
                  <h4 v-translate>Please wait</h4>
                </div>
              </div>
            </b-col>
          </b-row>
        </b-container>
      </div>
    </div>
  </div>
</template>

<script>
import AddressMixin from '@/mixins/address.mixin'
import XLSX from '@e965/xlsx'
import CompanyAutocomplete from '@/components/Autocompletes/CompanyAutocomplete'
import AddressAutocomplete from '@/components/Autocompletes/AddressAutocomplete'
import ShippingCheckpointForm from '@/components/Shippings/CheckpointForm'
import PlacePicker from '@/components/PlacePicker'

export default {
  name: 'ShippingBulkImport',
  components: {
    CompanyAutocomplete,
    AddressAutocomplete,
    ShippingCheckpointForm,
    PlacePicker
  },
  title() {
    return this.$gettext('Bulk Shippings Import')
  },
  data() {
    return {
      session: null,
      checking: false,
      checkingNumber: 0,
      checkingTotal: 0,
      checkingMeli: false,
      checkingSizes: false,
      checkingAddresses: false,
      checkingQuotes: false,
      creatingShippings: false,
      file: null,
      preCheckedShippings: [],
      fields: [
        {
          key: 'line',
          label: this.$gettext('Line')
        },
        {
          key: 'type',
          label: this.$gettext('Type')
        },
        {
          key: 'size',
          label: this.$gettext('Size')
        },
        {
          key: 'from',
          label: this.$gettext('Origin')
        },
        {
          key: 'to',
          label: this.$gettext('Destination')
        },
        {
          key: 'items',
          label: this.$gettext('Items')
        },
        {
          key: 'internalCode',
          label: this.$gettext('External ID')
        },
        {
          key: 'internalOrderId',
          label: this.$gettext('External Order ID')
        },
        {
          key: 'settings',
          label: this.$gettext('Extra Settings')
        },
        {
          key: 'validQuote',
          label: this.$gettext('Has quote?')
        },
        {
          key: 'actions',
          label: this.$gettext('Actions')
        }
      ],
      fatalErrors: null,
      scanErrors: null,
      meliErrors: null,
      sizeErrors: null,
      fromErrors: null,
      toErrors: null,
      quoteErrors: null,
      checkpoint: null,
      validApp: this.$store.getters.profile.company.applications.find(x => x.platform_id === null) || this.$store.getters.profile.company.applications.find(x => x.platform.name === 'Moova'),
      useVolumetric: false,
      shippingsCreated: [],
      step: 0,
      bulkImportFile: null,
      index: null,
      itemEdit: {},
      company: this.$store.getters.profile.company,
      validatedAddresses: {
        from: {},
        to: {}
      },
      validatedGeofences: {
        from: {},
        to: {}
      }
    }
  },
  watch: {
    meliErrors(val) {
      if (val < 0) {
        this.meliErrors = 0
      }
    },
    sizeErrors(val) {
      if (val < 0) {
        this.sizeErrors = 0
      }
    },
    fromErrors(val) {
      if (val < 0) {
        this.fromErrors = 0
      }
    },
    toErrors(val) {
      if (val < 0) {
        this.toErrors = 0
      }
    },
    quoteErrors(val) {
      if (val < 0) {
        this.quoteErrors = 0
      }
    }
  },
  mixins: [AddressMixin],
  mounted() {
    if (!this.validApp) {
      if (this.isGcmAdmin) {
        this.createDefaultApplication(this.profile.company)
      }
    }
    this.onSearchCompanySelected(this.profile.company)
  },
  methods: {
    async fileChanged(event) {
      this.fatalErrors = null
      this.scanErrors = null
      this.meliErrors = null
      this.sizeErrors = null
      this.fromErrors = null
      this.toErrors = null
      this.quoteErrors = null
      this.preCheckedShippings = []
      this.session = '_' + Math.random().toString(36).substr(2, 9)
      console.log(this.file)
      // this.file = this.$refs.importedShippings.selectedFile
      if (this.file) {
        console.log('ARGH')
        const formData = new FormData()
        formData.append('file', this.file, this.file.name)
        this.$http.post(this.$appConfig.api.baseUrl + 'companies/' + this.profile.company_id + '/files', formData)
          .then(response => {
            this.bulkImportFile = response.body
          }, error => { // no permissions for plain company user yet :/
            console.log(error)
            this.$root.$children[0].$refs.simplert.justCloseSimplert()
          })
        this.checking = true
        const reader = new FileReader()
        reader.onload = e => {
          let workbook
          workbook = XLSX.read(e.target.result, {
            type: 'buffer',
            raw: true,
            ...this.options
          })
          // try {
          //   console.log('Try CSV')
          //   workbook = XLSX.read(new TextDecoder('utf-8').decode(e.target.result), {
          //     type: 'string',
          //     raw: true,
          //     ...this.options
          //   })
          // } catch (error) {
          //   console.log('Go XLSX')
          //   workbook = XLSX.read(e.target.result, {
          //     type: 'buffer',
          //     raw: true,
          //     ...this.options
          //   })
          // }
          workbook.SheetNames.forEach(async function (sheetName, sheetIndex) {
            if (sheetIndex === 0) {
              let XLRowObject = XLSX.utils.sheet_to_row_object_array(workbook.Sheets[sheetName])
              let row = 1
              let preShippings = XLRowObject.map((x, i) => {
                row++
                if (((x['DESTINO.NOMBRE'] && (x['DESTINO.NOMBRE'] + '').toLowerCase() !== 'destino') || (x['DESTINO.CALLE'] && (x['DESTINO.CALLE'] + '').toLowerCase() !== 'direccion en partes. todos los campos requeridos.')) || x['to.contact.firstName'] || x['DESTINO.ID_GEOVALLA_EXTERNO'] || x['to.geoFenceExternalId'] || x['DESTINO.ID_GEOVALLA_MOOVA'] || x['to.geoFenceId']) {
                  let unflattened = this.unflattenObject(x)
                  let moovaObject = {
                    line: row,
                    reference: row,
                    type: x['TIPO'] || x['type'] || 'regular',
                    flow: x['FLUJO'] && x['FLUJO'] !== 'automatico' ? this.setFlow(x['FLUJO']) : (x['flow'] && x['flow'] !== 'automatic' ? x['flow'] : 'semi-automatic'),
                    size: x['TAMAÑO'] || x['size'] || 'm',
                    from: {
                      geoFenceId: x['ORIGEN.ID_GEOVALLA_MOOVA'] || x['from.geoFenceId'] || x['from.geo_fence_id'] || null,
                      geoFenceExternalId: x['ORIGEN.ID_GEOVALLA_EXTERNO'] || x['from.geoFenceExternalId'] || x['from.geo_fence_external_id'] || null,
                      placeId: x['ORIGEN.ID_LUGAR_MOOVA'] || x['from.placeId'] || x['from.place_id'] || null,
                      addressDescription: x['ORIGEN.REFERENCIA_DIRECCION'] || x['from.addressDescription'] || null,
                      coords: {
                        lat: x['ORIGEN.LATITUD'] || x['from.coords.lat'] || null,
                        lng: x['ORIGEN.LONGITUD'] || x['from.coords.lng'] || null
                      },
                      street: (x['ORIGEN.CALLE'] || x['from.street']) ? (x['ORIGEN.CALLE'] || x['from.street']) + '' : null,
                      number: x['ORIGEN.NUMERO'] || x['from.number'] || null,
                      floor: x['ORIGEN.PISO'] || x['from.floor'] || this.defaultShippingFields.floor || null,
                      apartment: x['ORIGEN.PUERTA'] || x['from.apartment'] || this.defaultShippingFields.apartment || null,
                      city: x['ORIGEN.CIUDAD'] || x['from.city'] || null,
                      state: x['ORIGEN.PROVINCIA'] || x['from.state'] || null,
                      postalCode: x['ORIGEN.CODIGO_POSTAL'] || x['from.postalCode'] || null,
                      country: x['ORIGEN.PAIS'] || x['from.country'] || null,
                      address: x['ORIGEN.DIRECCION'] || x['from.address'] || this.defaultShippingFields.googlePlaceString || null,
                      instructions: (x['ORIGEN.INSTRUCCIONES'] || x['from.instructions']) ? (x['ORIGEN.INSTRUCCIONES'] || x['from.instructions']) + '' : null,
                      contact: {
                        firstName: x['ORIGEN.NOMBRE'] || x['from.contact.firstName'] || this.defaultShippingFields.contact.firstName || null,
                        lastName: x['ORIGEN.APELLIDOS'] || x['from.contact.lastName'] || this.defaultShippingFields.contact.lastName || null,
                        email: x['ORIGEN.EMAIL'] || x['from.contact.email'] || this.defaultShippingFields.contact.email || null,
                        phone: x['ORIGEN.TELEFONO'] || x['from.contact.phone'] || this.defaultShippingFields.phone ? this.$options.filters.phone(this.preFormatPhone((x['ORIGEN.TELEFONO'] || x['from.contact.phone']) + '' || this.defaultShippingFields.contact.phone), this.$store.getters.profile.company.country_iso_code) : null
                      },
                      message: '',
                      _editing: false
                    },
                    to: {
                      geoFenceId: x['DESTINO.ID_GEOVALLA_MOOVA'] || x['to.geoFenceId'] || x['to.geo_fence_id'] || null,
                      geoFenceExternalId: x['DESTINO.ID_GEOVALLA_EXTERNO'] || x['to.geoFenceExternalId'] || x['to.geo_fence_external_id'] || null,
                      placeId: x['DESTINO.ID_LUGAR_MOOVA'] || x['to.placeId'] || x['to.place_id'] || null,
                      addressDescription: x['DESTINO.REFERENCIA_DIRECCION'] || x['to.addressDescription'] || null,
                      coords: {
                        lat: x['DESTINO.LATITUD'] || x['to.coords.lat'] || null,
                        lng: x['DESTINO.LONGITUD'] || x['to.coords.lng'] || null
                      },
                      street: (x['DESTINO.CALLE'] || x['to.street']) ? (x['DESTINO.CALLE'] || x['to.street']) + '' : null,
                      number: x['DESTINO.NUMERO'] || x['to.number'] || null,
                      floor: x['DESTINO.PISO'] || x['to.floor'] || null,
                      apartment: x['DESTINO.PUERTA'] || x['to.apartment'] || null,
                      city: x['DESTINO.CIUDAD'] || x['to.city'] || null,
                      state: x['DESTINO.PROVINCIA'] || x['to.state'] || null,
                      postalCode: x['DESTINO.CODIGO_POSTAL'] || x['to.postalCode'] || null,
                      country: x['DESTINO.PAIS'] || x['to.country'] || null,
                      address: x['DESTINO.DIRECCION'] || x['to.address'] || null,
                      instructions: (x['DESTINO.INSTRUCCIONES'] || x['to.instructions']) ? (x['DESTINO.INSTRUCCIONES'] || x['to.instructions']) + '' : null,
                      contact: {
                        firstName: x['DESTINO.NOMBRE'] || x['to.contact.firstName'] || null,
                        lastName: x['DESTINO.APELLIDOS'] || x['to.contact.lastName'] || null,
                        email: x['DESTINO.EMAIL'] || x['to.contact.email'] || null,
                        phone: x['DESTINO.TELEFONO'] || x['to.contact.phone'] ? this.$options.filters.phone(this.preFormatPhone((x['DESTINO.TELEFONO'] || x['to.contact.phone']) + ''), this.$store.getters.profile.company.country_iso_code) : null
                      },
                      message: '',
                      _editing: false
                    },
                    internalCode: x['ID_INTERNO/NUMERO_DE_ENVIO_FLEX'] || x['internalCode'] || null,
                    internalOrderId: x['ID_DE_ORDEN'] || x['internalOrderId'] || null,
                    description: x['DESCRIPCION'] || x['description'] || null,
                    label: '',
                    extra: {},
                    settings: [],
                    returnSettings: [],
                    conf: {
                      assurance: false
                    }
                  }
                  let items = []
                  if (unflattened['ITEMS'] && unflattened['ITEMS'].length > 0) {
                    items = unflattened['ITEMS']
                  } else if (unflattened['items'] && unflattened['items'].length > 0) {
                    items = unflattened['items']
                  } else if (unflattened['conf'] && unflattened['conf']['items'] && unflattened['conf']['items'].length > 0) {
                    items = unflattened['conf']['items'].map(x => {
                      return x.item ? x.item : x
                    })
                  }
                  if (items.length > 0) {
                    moovaObject.items = items.map(y => {
                      return {
                        description: y['DESCRIPCION'] || y['description'] || null,
                        reference_code: y['CODIGO_REF'] || y['reference_code'] || null,
                        weight: y['PESO'] || y['weight'] || 0,
                        height: y['ALTO'] || y['height'] || 0,
                        width: y['ANCHO'] || y['width'] || 0,
                        length: y['LARGO'] || y['length'] || 0,
                        price: y['PRECIO'] || y['price'] || null,
                        currency: y['PRECIO'] || y['price'] ? y['MONEDA'] || y['currency'] || window.countries.find(z => z.iso_code === this.$store.getters.profile.company.country_iso_code).currency : null,
                        quantity: y['CANTIDAD'] || y['quantity'] || 1
                      }
                    })
                  }
                  // console.log(this.shippingSettings)
                  if ((unflattened['IDA_Y_VUELTA'] && unflattened['IDA_Y_VUELTA'] === 'si') || (unflattened['settings'] && unflattened['settings'].indexOf('BackAndForth') !== -1)) {
                    moovaObject.settings.push(this.shippingSettings.find(x => x.text === 'BackAndForth').value)
                  }
                  if ((unflattened['FIRMA_EN_PICKUP'] && unflattened['FIRMA_EN_PICKUP'] === 'si') || (unflattened['settings'] && unflattened['settings'].indexOf('PickupSignature') !== -1)) {
                    moovaObject.settings.push(this.shippingSettings.find(x => x.text === 'PickupSignature').value)
                  }
                  if ((unflattened['FOTO_EN_PICKUP'] && unflattened['FOTO_EN_PICKUP'] === 'si') || (unflattened['settings'] && unflattened['settings'].indexOf('PickupPhoto') !== -1)) {
                    moovaObject.settings.push(this.shippingSettings.find(x => x.text === 'PickupPhoto').value)
                  }
                  if ((unflattened['SCAN_NUMERO_SERIE'] && unflattened['SCAN_NUMERO_SERIE'] === 'si') || (unflattened['settings'] && unflattened['settings'].indexOf('UpdateDeliveredItems') !== -1)) {
                    moovaObject.settings.push(this.shippingSettings.find(x => x.text === 'UpdateDeliveredItems').value)
                    moovaObject.validScan = typeof moovaObject.items !== 'undefined' && moovaObject.items.length > 0
                    if (moovaObject.validScan === false) {
                      moovaObject._rowVariant = 'danger'
                    }
                  } else {
                    moovaObject.validScan = true
                  }

                  // Ecosystem forced settings
                  if (this.ecosystemConfig && this.ecosystemConfig.forcedShippingSettings && this.ecosystemConfig.forcedShippingSettings.length > 0) {
                    let settings = this.ecosystemConfig.forcedShippingSettings.map(x => {
                      return this.shippingSettings.find(y => y.text === x).value
                    })
                    moovaObject.settings = [...new Set((moovaObject.settings || []).concat(settings))]
                  }

                  if (moovaObject.type === 'next_day') {
                    moovaObject.flow = 'manual'
                  }
                  return moovaObject
                }
                return null
              }).filter(Boolean)
              if (preShippings.length > 0) {
                this.checkingSizes = true
                this.step++
                let prePreCheckedShippings = await this.checkSizes(preShippings)
                this.checkingSizes = false
                this.checkingMeli = true
                prePreCheckedShippings = await this.checkMeli(prePreCheckedShippings)
                this.checkingMeli = false
                this.checkingAddresses = true
                this.step++
                this.preCheckedShippings = await this.preShippingsCheck(prePreCheckedShippings)
                this.checking = false
                this.checkingAddresses = false
                this.session = null
                this.checkForErrors()
              } else {
                this.checking = false
                this.fatalErrors = true
              }
              this.checking = false
              this.checkingAddresses = false
            }
          }.bind(this))
        }
        reader.onerror = e => {
          console.log(e)
        }
        reader.readAsArrayBuffer(this.file)
      }
    },
    setFlow(rawFlow) {
      const flowsMap = {
        manual: 'manual',
        automatico: 'automatic',
        'semi-automatico': 'semi-automatic',
        deposito: 'warehouse'
      }
      return flowsMap[rawFlow]
    },
    async checkMeli(shippings) {
      if (shippings.length > 0) {
        this.checkingTotal = shippings.length
        for (let i = 0; i < shippings.length; i++) {
          this.checkingNumber = i + 1
          if (shippings[i].type === 'mercado_flex') {
            if (shippings[i].internalCode) {
              shippings[i].validMeli = true
            } else {
              shippings[i].validMeli = false
              shippings[i]._rowVariant = 'danger'
              this.meliErrors++
            }
          } else {
            shippings[i].validMeli = true
          }
        }
        return shippings
      }
    },
    async checkSizes(shippings) {
      if (shippings.length > 0) {
        this.checkingTotal = shippings.length
        const chunkSize = 100
        let shippingsCollection = [...new Set(shippings)]
        const chunks = [...Array(Math.ceil(shippingsCollection.length / chunkSize))].map(_ => shippingsCollection.splice(0, chunkSize))
        let checkedShippings = []
        for (let i = 0; i < chunks.length; i++) {
          checkedShippings = checkedShippings.concat(await this.$http.post(this.$appConfig.api.baseUrl + 'sizes/calculate', chunks[i])
            .then(response => {
              this.checkingNumber = i * chunkSize
              return chunks[i].map(x => {
                if (!response.body[x.reference].error) {
                  x.sizeId = response.body[x.reference].id
                  x.size = response.body[x.reference].name
                  x.validSize = true
                } else {
                  x.validSize = false
                  x._rowVariant = 'danger'
                }
                return x
              })
            })
          )
        }
        this.checkingSizes = false
        return checkedShippings
      }
    },
    async preShippingsCheck(shippings) {
      this.checkingTotal = shippings.length
      for (let i = 0; i < shippings.length; i++) {
        this.checkingNumber = i + 1
        shippings[i] = await this.validateAddress(shippings[i], 'from')
        shippings[i] = await this.validateAddress(shippings[i], 'to')

        this.$root.$children[0].$refs.simplert.justCloseSimplert()
      }
      return shippings
    },
    async validateAddress(shipping, checkpoint = 'to') {
      let hash = JSON.parse(JSON.stringify(shipping[checkpoint]))
      delete hash.contact
      delete hash.instructions
      delete hash.message
      hash = JSON.stringify(hash)
      if (!this.validatedAddresses[checkpoint][hash]) {
        this.validatedAddresses[checkpoint][hash] = {}
      } else {
        shipping[checkpoint] = {...shipping[checkpoint], ...this.validatedAddresses[checkpoint][hash]}
        if (this.validatedAddresses[checkpoint][hash].placeId || this.validatedAddresses[checkpoint][hash].geoFenceId) {
          shipping['valid' + checkpoint.charAt(0).toUpperCase() + checkpoint.slice(1)] = true
        } else {
          shipping['valid' + checkpoint.charAt(0).toUpperCase() + checkpoint.slice(1)] = false
          this[checkpoint + 'Errors'] = true
        }
        if (this.validatedGeofences[checkpoint][hash].id) {
          if (!shipping.conf.geofences) {
            shipping.conf = {...shipping.conf, geofences: {[checkpoint]: this.validatedGeofences[checkpoint][hash]}}
          } else {
            shipping.conf.geofences = {...shipping.conf.geofences, [checkpoint]: this.validatedGeofences[checkpoint][hash]}
          }
        }
        return shipping
      }
      if (!this.validatedGeofences[checkpoint][hash]) {
        this.validatedGeofences[checkpoint][hash] = {}
      }
      if (this.validGeoFenceId(shipping, checkpoint)) {
        await this.checkGeoFenceById(shipping[checkpoint].geoFenceId)
          .then(response => {
            if (this.theCheckedGeoFence && this.theCheckedGeoFence.id) {
              shipping[checkpoint].geoFenceId = this.theCheckedGeoFence.id
              shipping[checkpoint].contact.firstName = this.theCheckedGeoFence.external_id + ' - ' + this.theCheckedGeoFence.name
              shipping[checkpoint].address = '@' + this.getCenter(this.theCheckedGeoFence.fence.coordinates[0].map(x => [x[1], x[0]])).join(',') + ',17z' // + this.theCheckedGeoFence.name
              if (!shipping.conf.geofences) {
                shipping.conf = {...shipping.conf, geofences: {[checkpoint]: this.theCheckedGeoFence}}
              } else {
                shipping.conf.geofences = {...shipping.conf.geofences, [checkpoint]: this.theCheckedGeoFence}
              }
              this.validatedGeofences[checkpoint][hash] = this.theCheckedGeoFence
              shipping['valid' + checkpoint.charAt(0).toUpperCase() + checkpoint.slice(1)] = true
            }
          })
      } else if (this.validGeoFenceExternalId(shipping, checkpoint)) {
        await this.checkGeoFenceByExternalId(shipping[checkpoint].geoFenceExternalId)
          .then(response => {
            if (this.theCheckedGeoFence && this.theCheckedGeoFence.id) {
              shipping[checkpoint].geoFenceId = this.theCheckedGeoFence.id
              shipping[checkpoint].contact.firstName = this.theCheckedGeoFence.external_id + ' - ' + this.theCheckedGeoFence.name
              shipping[checkpoint].address = '@' + this.getCenter(this.theCheckedGeoFence.fence.coordinates[0].map(x => [x[1], x[0]])).join(',') + ',17z' // + this.theCheckedGeoFence.name
              if (!shipping.conf.geofences) {
                shipping.conf = {...shipping.conf, geofences: {[checkpoint]: this.theCheckedGeoFence}}
              } else {
                shipping.conf.geofences = {...shipping.conf.geofences, [checkpoint]: this.theCheckedGeoFence}
              }
              this.validatedGeofences[checkpoint][hash] = this.theCheckedGeoFence
              shipping['valid' + checkpoint.charAt(0).toUpperCase() + checkpoint.slice(1)] = true
            }
          })
      } else if (this.validPlaceId(shipping, checkpoint)) {
        shipping[checkpoint].address = await this.checkPlace({id: shipping[checkpoint].placeId}, this.session).then(response => { return response.formatted_address })
        shipping['valid' + checkpoint.charAt(0).toUpperCase() + checkpoint.slice(1)] = true
      } else if (this.validCoords(shipping, checkpoint)) {
        let currentShipping = shipping
        await this.checkPlaceByCoords(shipping[checkpoint].coords.lat, shipping[checkpoint].coords.lng, this.session)
          .then(response => {
            console.log(response)
            if (this.theCheckedAddress.is_valid) {
              currentShipping[checkpoint].address = this.theCheckedAddress.formatted_address
              currentShipping[checkpoint].placeId = this.theCheckedAddress.id
              currentShipping['valid' + checkpoint.charAt(0).toUpperCase() + checkpoint.slice(1)] = true
            } else {
              currentShipping[checkpoint].address = currentShipping[checkpoint].addressDescription
              currentShipping['valid' + checkpoint.charAt(0).toUpperCase() + checkpoint.slice(1)] = false
              this[checkpoint + 'Errors'] = true
            }
          })
      } else if (this.validAddressParts(shipping, checkpoint) || this.validWholeAddress(shipping, checkpoint)) {
        const plusCodesRegexp = new RegExp(/(^|\s)([23456789CFGHJMPQRVWX]{4,6}\+[23456789CFGHJMPQRVWX]{2,3})(\W+\w+)+(\s|$)|(^|\s)([23456789C][23456789CFGHJMPQRV][23456789CFGHJMPQRVWX]{6}\+[23456789CFGHJMPQRVWX]{2,3})(\s|$)/i)

        let params = {
          query: this.validAddressParts(shipping, checkpoint) ? shipping[checkpoint].street + ' ' + shipping[checkpoint].number + ', ' + shipping[checkpoint].city + ', ' + shipping[checkpoint].state : shipping[checkpoint].address,
          session_token: this.session,
          country: this.$store.getters.profile.company.country_iso_code
        }
        if (!plusCodesRegexp.test(shipping[checkpoint].address)) {
          await this.$http.get(this.$appConfig.api.baseUrl + 'autocomplete', {params: params})
            .then(response => {
              if (response.body.length === 1) {
                return this.checkPlaces(response.body, this.session)
                  .then(response => {
                    if (response) {
                      shipping[checkpoint].address = response.formatted_address
                      shipping[checkpoint].placeId = response.id
                      shipping['valid' + checkpoint.charAt(0).toUpperCase() + checkpoint.slice(1)] = true
                    } else {
                      this.$root.$children[0].$refs.simplert.justCloseSimplert()
                      shipping[checkpoint].address = params.query
                      shipping[checkpoint].checkedStringAddress = params.query
                      shipping['valid' + checkpoint.charAt(0).toUpperCase() + checkpoint.slice(1)] = false
                      this[checkpoint + 'Errors'] = true
                    }
                  })
              } else if (response.body.length > 1) {
                return this.checkPlaces(response.body, this.session)
                  .then(response => {
                    if (response) {
                      shipping[checkpoint].address = response.formatted_address
                      shipping[checkpoint].placeId = response.id
                      shipping['valid' + checkpoint.charAt(0).toUpperCase() + checkpoint.slice(1)] = true
                      shipping[checkpoint].checkedStringAddress = params.query
                    } else {
                      this.$root.$children[0].$refs.simplert.justCloseSimplert()
                      shipping[checkpoint].address = params.query
                      shipping[checkpoint].checkedStringAddress = params.query
                      shipping['valid' + checkpoint.charAt(0).toUpperCase() + checkpoint.slice(1)] = false
                      this[checkpoint + 'Errors'] = true
                    }
                  })
              } else {
                shipping[checkpoint].address = params.query
                shipping[checkpoint].checkedStringAddress = params.query
                shipping['valid' + checkpoint.charAt(0).toUpperCase() + checkpoint.slice(1)] = false
                this[checkpoint + 'Errors'] = true
              }
            })
        } else {
          await this.checkPlaces([{code: shipping[checkpoint].address, plusCode: true}], this.session)
            .then(response => {
              if (response) {
                shipping[checkpoint].address = response.formatted_address
                shipping[checkpoint].placeId = response.id
                shipping['valid' + checkpoint.charAt(0).toUpperCase() + checkpoint.slice(1)] = true
              } else {
                this.$root.$children[0].$refs.simplert.justCloseSimplert()
                shipping[checkpoint].address = params.query
                shipping[checkpoint].checkedStringAddress = params.query
                shipping['valid' + checkpoint.charAt(0).toUpperCase() + checkpoint.slice(1)] = false
                this[checkpoint + 'Errors'] = true
              }
            })
        }
      } else {
        shipping[checkpoint].checkedStringAddress = shipping[checkpoint].address ||
          (shipping[checkpoint].street ? shipping[checkpoint].street + ', ' : '') +
          (shipping[checkpoint].number ? shipping[checkpoint].number + ', ' : '') +
          (shipping[checkpoint].city ? shipping[checkpoint].city + ', ' : '') +
          (shipping[checkpoint].state || '')
        this.$root.$children[0].$refs.simplert.justCloseSimplert()
        shipping['valid' + checkpoint.charAt(0).toUpperCase() + checkpoint.slice(1)] = false
        this[checkpoint + 'Errors'] = true
      }

      let validatedAddress = JSON.parse(JSON.stringify(shipping[checkpoint]))
      // delete validatedAddress.contact
      delete validatedAddress.instructions
      delete validatedAddress.message
      this.validatedAddresses[checkpoint][hash] = validatedAddress

      return shipping
    },
    validPlaceId(shipping, checkpoint = 'to') {
      return Boolean(shipping[checkpoint].placeId)
    },
    validGeoFenceId(shipping, checkpoint = 'to') {
      return Boolean(shipping[checkpoint].geoFenceId)
    },
    validGeoFenceExternalId(shipping, checkpoint = 'to') {
      return Boolean(shipping[checkpoint].geoFenceExternalId)
    },
    validCoords(shipping, checkpoint = 'to') {
      return shipping[checkpoint].coords.lat && shipping[checkpoint].coords.lng
    },
    validAddressParts(shipping, checkpoint = 'to') {
      return shipping[checkpoint].street && shipping[checkpoint].number && shipping[checkpoint].city && shipping[checkpoint].state
    },
    validWholeAddress(shipping, checkpoint = 'to') {
      return Boolean(shipping[checkpoint].address)
    },
    selectedAddress(item, checkpoint, index) {
      if (item.reference) {
        if (index) {
          this.preCheckedShippings[index][checkpoint].contact.firstName = item.contact
          this.preCheckedShippings[index][checkpoint].contact.email = item.email
          this.preCheckedShippings[index][checkpoint].contact.phone = item.phone
          this.preCheckedShippings[index][checkpoint].floor = item.address_extra
          this.doCheckPlace({id: item.place_id, main_text: item.place.formatted_address, secondary_text: ''}, checkpoint, index)
        } else {
          this.itemEdit[checkpoint].contact.firstName = item.contact
          this.itemEdit[checkpoint].contact.email = item.email
          this.itemEdit[checkpoint].contact.phone = item.phone
          this.itemEdit[checkpoint].floor = item.address_extra
          this.doCheckPlace({id: item.place_id, main_text: item.place.formatted_address, secondary_text: ''}, checkpoint)
        }
      } else if (index) {
        this.doCheckPlace(item, checkpoint, index)
      } else {
        this.doCheckPlace(item, checkpoint)
      }
    },
    async doCheckPlace(place, checkpoint, index) {
      await this.checkPlace(place)
      this.$nextTick(() => {
        if (this.theCheckedAddress.id) {
          if (index) {
            this.preCheckedShippings[index][checkpoint].address = this.theCheckedAddress.formatted_address
            this.setPlaceId(this.theCheckedAddress.id, checkpoint, index)
          } else {
            this.itemEdit[checkpoint].address = this.theCheckedAddress.formatted_address
            this.setPlaceId(this.theCheckedAddress.id, checkpoint)
          }
          this.theCheckedAddress = {}
        } else if (this.theCheckedAddress.validAddress === false) {
          this.checkpoint = checkpoint
          this.index = index
          this.$refs.modalFromPickPlace.show()
          setTimeout(function () {
            this.$refs.pickFromPlaceMap.center = {
              lat: this.theCheckedAddress.lat,
              lng: this.theCheckedAddress.lng
            }
            this.$refs.pickFromPlaceMap.reference = this.$refs['address_' + checkpoint + '_' + index].searchQuery
            this.$refs.pickFromPlaceMap.styles = 'height: 500px;'
          }.bind(this), 100)
        }
      })
    },
    setPlaceId(placeId, checkpoint, index) {
      if (index) {
        this.preCheckedShippings[index][checkpoint].placeId = placeId
        this[checkpoint + 'Errors'] = this.preCheckedShippings[index]['valid' + checkpoint.charAt(0).toUpperCase() + checkpoint.slice(1)] === false ? this[checkpoint + 'Errors'] - 1 : this[checkpoint + 'Errors']
        this.preCheckedShippings[index]['valid' + checkpoint.charAt(0).toUpperCase() + checkpoint.slice(1)] = true
        this.preCheckedShippings[index][checkpoint]._editing = false
      } else {
        this.itemEdit[checkpoint].placeId = placeId
        // set the valid counters into editItem()
      }
    },
    setPlacePickedFrom(place) {
      if (this.index) {
        this.preCheckedShippings[this.index][this.checkpoint].address = place.formatted_address
        this.preCheckedShippings[this.index][this.checkpoint].instructions = this.preCheckedShippings[this.index][this.checkpoint].instructions ? this.preCheckedShippings[this.index][this.checkpoint].instructions + ' - ' + place.reference : place.reference
        this.setPlaceId(place.id, this.checkpoint, this.index)
      } else {
        this.itemEdit[this.checkpoint].address = place.formatted_address
        this.$refs['address_' + this.checkpoint + '_undefined'].searchQuery = place.formatted_address
        this.itemEdit[this.checkpoint].instructions = this.itemEdit[this.checkpoint].instructions ? this.itemEdit[this.checkpoint].instructions + ' - ' + place.reference : place.reference
        this.setPlaceId(place.id, this.checkpoint)
      }
      this.$refs.modalFromPickPlace.hide()
    },
    closeModalFromPickPlace() {
      this.$refs.modalFromPickPlace.hide()
    },
    checkForErrors() {
      this.scanErrors = this.preCheckedShippings.filter(x => x.validScan === false).length
      this.sizeErrors = this.preCheckedShippings.filter(x => x.validSize === false).length
      this.meliErrors = this.preCheckedShippings.filter(x => x.validMeli === false).length
      this.fromErrors = this.preCheckedShippings.filter(x => x.validFrom === false).length
      this.toErrors = this.preCheckedShippings.filter(x => x.validTo === false).length
      this.quoteErrors = this.preCheckedShippings.filter(x => x.validQuote === false).length
      if (this.preCheckedShippings.length === 0) {
        this.resetImport()
      }
    },
    editItem() {
      this[this.checkpoint + 'Errors'] = this.itemEdit['valid' + this.checkpoint.charAt(0).toUpperCase() + this.checkpoint.slice(1)] === false ? this[this.checkpoint + 'Errors'] - 1 : this[this.checkpoint + 'Errors']
      this.preCheckedShippings[this.preCheckedShippings.findIndex(x => x.line === this.itemEdit.line)]['valid' + this.checkpoint.charAt(0).toUpperCase() + this.checkpoint.slice(1)] = true
      this.preCheckedShippings[this.preCheckedShippings.findIndex(x => x.line === this.itemEdit.line)][this.checkpoint] = this.itemEdit[this.checkpoint]
      this.checkForErrors()
      this.$refs.modalEditData.hide()
    },
    deleteRow(index) {
      if (
        confirm(this.$gettext('Are you sure you want to DELETE the selected row?'))
      ) {
        let toExport = Object.assign({}, this.preCheckedShippings[index])
        let line = toExport.line
        delete toExport.line
        delete toExport.reference
        delete toExport.validScan
        delete toExport.validSize
        delete toExport.validMeli
        delete toExport.validFrom
        delete toExport.from.checkedStringAddress
        delete toExport.from._editing
        delete toExport.validTo
        delete toExport.to.checkedStringAddress
        delete toExport.to._editing
        delete toExport.validQuote
        delete toExport._rowVariant
        toExport.settings = toExport.settings.map(x => {
          return this.shippingSettings.find(y => y.id === x).name
        }).join('|')
        toExport = [this.flattenObject(toExport)]
        this.download(this.toCsv(toExport), 'text/csv', this.file.name.split('.').slice(0, -1).join('.') + this.$gettext('_line_') + line + this.$gettext('_deleted_on_') + this.moment().local().format('YYYYMMDD_HHmmss') + '.csv')
        this.preCheckedShippings.splice(index, 1)
        this.checkForErrors()
      }
    },
    deleteAllWithErrors(checkpoint) {
      let toExport = this.preCheckedShippings.filter(x => x['valid' + checkpoint.charAt(0).toUpperCase() + checkpoint.slice(1)] === false)
        .map(x => {
          delete x.line
          delete x.reference
          delete x.validScan
          delete x.validSize
          delete x.validMeli
          delete x.validFrom
          delete x.from.checkedStringAddress
          delete x.from._editing
          delete x.validTo
          delete x.to.checkedStringAddress
          delete x.to._editing
          delete x.validQuote
          delete x._rowVariant
          x.settings = x.settings.map(y => {
            return this.shippingSettings.find(z => z.id === y).name
          }).join('|')
          x = this.flattenObject(x)
          return x
        })
      this.download(this.toCsv(toExport), 'text/csv', this.file.name.split('.').slice(0, -1).join('.') + this.$gettext('_deleted_on_') + this.moment().local().format('YYYYMMDD_HHmmss') + '.csv')
      this.preCheckedShippings = this.preCheckedShippings.filter(x => x['valid' + checkpoint.charAt(0).toUpperCase() + checkpoint.slice(1)] === true)
      this.checkForErrors()
    },
    successModalConfirmation(item) {
      console.log(item)
      this.preCheckedShippings = this.preCheckedShippings.map(x => {
        if (!x['valid' + this.checkpoint.charAt(0).toUpperCase() + this.checkpoint.slice(1)]) {
          x[this.checkpoint].placeId = item.from_id || item.from_place_id
          x[this.checkpoint].address = item.from_formatted_address
          x[this.checkpoint].floor = item.from_extra
          x[this.checkpoint].instructions = item.from_meta
          x[this.checkpoint].contact.firstName = item.from_contact
          x[this.checkpoint].contact.email = item.from_email
          x[this.checkpoint].contact.phone = item.from_phone
          x['valid' + this.checkpoint.charAt(0).toUpperCase() + this.checkpoint.slice(1)] = true
        }
        return x
      })
      this[this.checkpoint + 'Errors'] = 0
      this.closeModalConfirmation()
      this.checkpoint = null
    },
    closeModalConfirmation() {
      this.$refs.modalConfirmation.hide()
    },
    async checkQuotes() {
      this.checking = true
      this.checkingQuotes = true
      this.checkingNumber = 0
      this.checkingTotal = this.preCheckedShippings.length
      this.step++

      for (let i = 0; i < this.preCheckedShippings.length; i++) {
        let index = i
        let payload = {...this.preCheckedShippings[i]}
        let newFrom = {
          placeId: payload.from.placeId,
          floor: payload.from.floor,
          apartment: payload.from.apartment,
          contact: payload.from.contact
        }
        let newTo = {
          placeId: payload.to.placeId,
          floor: payload.to.floor,
          apartment: payload.to.apartment,
          contact: payload.to.contact
        }
        payload.from = newFrom
        payload.to = newTo

        await this.$http.post(`${this.$appConfig.api.baseUrl}b2b/v2/budgets?appId=${this.validApp.id}`, payload, {before: this.changeAuth})
          .then(response => {
            this.preCheckedShippings[index].validQuote = true
            this.preCheckedShippings[index].price = response.body.price_formatted
          }, () => {
            this.$root.$children[0].$refs.simplert.justCloseSimplert()
            this.preCheckedShippings[index].validQuote = false
          })
          .finally(() => {
            this.checkingNumber = index + 1
            if (index + 1 === this.preCheckedShippings.length) {
              this.checking = false
              this.checkingQuotes = false
              this.quoteErrors = this.preCheckedShippings.filter(x => x.validQuote === false).length
            }
          })
      }
    },
    async createShippings() {
      if (this.validApp.id) {
        try {
          const promiseStartShipments = []
          this.shipmentsCreated = []
          this.checking = true
          this.creatingShippings = true
          this.checkingTotal = this.preCheckedShippings.length
          this.checkingNumber = 0
          this.step++
          for (const shipping of this.preCheckedShippings) {
            promiseStartShipments.push(this.createShipping(shipping))
          }

          this.shippingsCreated = await Promise.all(promiseStartShipments)
        } catch (error) {
          console.log('try catch')
          console.log(error)
        } finally {
          this.resetImport()
        }
      } else {
        let obj = {
          title: null,
          message: this.$gettext('Please, contact us to get this service enabled:') + ' <strong><a href="mailto:help@moova.io">help@moova.io</a></strong>',
          type: 'warning',
          customCloseBtnText: this.$gettext('Close')
        }
        this.$root.$children[0].$refs.simplert.openSimplert(obj)
      }
    },
    async createShipping(payload) {
      if (this.validApp.id) {
        try {
          delete payload.from.coords
          delete payload.from.addressDescription
          delete payload.to.coords
          delete payload.to.addressDescription
          payload.conf.bulkImportFile = this.bulkImportFile
          payload.from.country = this.$store.getters.profile.company.country_iso_code
          payload.to.country = this.$store.getters.profile.company.country_iso_code
          if (payload.items) {
            payload.items.forEach(obj => Object.keys(obj).forEach((k) => obj[k] === null && delete obj[k]))
          }
          let shipping = await this.$http.post(`${this.$appConfig.api.baseUrl}b2b/shippings?appId=${this.validApp.id}`, payload, {before: this.changeAuth})
          this.checkingNumber++
          return {
            id: '#' + this.$options.filters.uuid(shipping.body.id),
            status: this.$gettext('Success'),
            link: shipping.body.id
          }
        } catch (e) {
          this.checkingNumber++
          this.$root.$children[0].$refs.simplert.justCloseSimplert()
          // discern error message
          let message
          if (e.status > 401 && e.status <= 500) {
            if (e.status === 422 && e.body.errors) {
              for (let i = 0; i < e.body.errors.length; i++) {
                message += '- ' + e.body.errors[i].detail + '<br>'
              }
            } else {
              message = e.body.message
            }
          } else if (e.status === 0 || e.status >= 500) {
            message = this.$gettext('Server Error') + (e.body.message ? ' - ' + e.body.message : '')
          }
          return {
            id: '-',
            status: this.$gettext('Failed'),
            link: null,
            csvError: this.$gettextInterpolate(this.$gettext('Line in file: %{line}<br/>Destination: %{address}<br/>Error: %{error}'), {
              line: payload.line,
              address: payload.to.address,
              error: e.status + ' - ' + message
            }),
            _rowVariant: 'danger'
          }
        }
      }
    },
    changeAuth(req) {
      return req.headers.set('Authorization', this.validApp.key)
    },
    resetEditing() {
      this.preCheckedShippings = this.preCheckedShippings.map(x => {
        x.from._editing = false
        x.to._editing = false
        return x
      })
    },
    preResetImport() {
      if (
        confirm(this.$gettext('Are you sure you want to RESTART the bulk import now?\n\n*** WARNING***\nThis will delete all the current data and CAN NOT BE UNDONE!'))
      ) {
        this.resetImport()
      }
    },
    resetImport() {
      this.onSearchCompanySelected(this.profile.company)
      this.preCheckedShippings = []
      if (this.$refs.importedShippings) {
        this.$refs.importedShippings.reset()
      }
      this.fatalErrors = null
      this.file = null
      this.creatingShippings = false
      this.checkingTotal = 0
      this.checkingNumber = 0
      this.checking = false
      this.step = 0
      this.index = null
      this.$refs.modalConfirmation.hide()
      if (this.shippingsCreated.length > 0) {
        this.$refs.createShippingSuccessMessage.show()
      }
    },
    createDefaultApplication(company) {
      let params = {
        name: company.name,
        company_id: company.id,
        active: true
      }
      this.$http.post(this.$appConfig.api.baseUrl + 'applications', params)
        .then(response => {
          this.$http.get(this.$appConfig.api.baseUrl + 'companies/' + company.id + '?includes[]=applications')
            .then(response => {
              this.onSearchCompanySelected(response.body)
            })
        })
    },
    onSearchCompanySelected(item) {
      this.validApp = item.applications.find(x => x.platform_id === null) || item.applications.find(x => x.platform && x.platform.name === 'Moova')

      if (!this.validApp || this.validApp.active !== 1) {
        this.createDefaultApplication(item)
        return
      } else {
        this.company = item
        if (!item.default_shipping_fields || item.default_shipping_fields.length === 0) {
          this.defaultShippingFields.contact.firstName = item.contact_name
          this.defaultShippingFields.contact.email = item.email
          this.defaultShippingFields.contact.phone = item.phone
          this.defaultShippingFields.country = this.shipping.from.country = this.shipping.to.country = item.country_iso_code
        } else {
          this.defaultShippingFields = item.default_shipping_fields
          this.company.defaultShippingFields = item.default_shipping_fields
          this.company.defaultShippingFields.country = this.defaultShippingFields.country = item.country_iso_code
        }
        this.searchCompanyQuery = ''
      }
    },
    preFormatPhone(phone) {
      if (phone.indexOf('+' + this.frontConfig.i18n.phonePrefix) !== -1 || phone.indexOf('+') === 0) {
        return phone
      }
      return phone.indexOf(this.frontConfig.i18n.phonePrefix) === 0 ? '+' + phone : '+' + this.frontConfig.i18n.phonePrefix + phone
    },
    openMapOn(placeString) {
      window.open('https://www.google.com/maps/place/' + placeString)
    },
    readableMetric(g) {
      const amt = g / 1000 * Math.pow(1000, 3)
      const s = ['mcg', 'mg', 'g', 'kg', 'ton']
      const e = Math.floor(Math.log10(amt) / Math.log10(1000))
      return (amt / Math.pow(1000, e)) + s[e]
    },
    getCenter(coordinates) {
      return coordinates.reduce(function (x, y) {
        return [x[0] + y[0] / coordinates.length, x[1] + y[1] / coordinates.length]
      }, [0, 0])
    }
  }
}
</script>

<style lang="scss">
.modal-need-application {
  position: fixed;
  z-index: 1002;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  background: rgba(255, 255, 255, .8);
  .modal-content {
    color: #856404 !important;
    background-color: #fff3cd !important;
    border-color: #ffeeba !important;
    display: flex;
  }
}
</style>
